import { useState } from 'react';
import { Search, X } from 'lucide-react';
import { useStockPhotosStore } from './stockPhotos-store';
import { useCanvasStore } from '../../store/canvas-store';
import { searchStock, type StockResult } from '../../assets/stockSearch';
import { importStockAsset } from '../../assets/importStockAsset';
import { invalidateCanvas } from '../../render/canvasInvalidate';
import type { Command } from '../../store/types';

/** Canvas coords at the center of the current viewport, for placing a new frame. */
function placeOrigin(): { x: number; y: number } {
  const { pan, zoom } = useCanvasStore.getState().doc.viewport;
  const cx = typeof window !== 'undefined' ? window.innerWidth / 2 : 0;
  const cy = typeof window !== 'undefined' ? window.innerHeight / 2 : 0;
  return { x: (cx - pan.x) / zoom, y: (cy - pan.y) / zoom };
}

/**
 * User-driven stock-photo picker. Type a query → grid of thumbnails → click to
 * import (re-host via /import) and place an `<img src="asset://…">` frame.
 * Mirrors the agent's `place_stock_photo` html + attribution handling.
 */
export function StockPhotoModal() {
  const open = useStockPhotosStore((s) => s.open);
  const setOpen = useStockPhotosStore((s) => s.setOpen);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<StockResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [placingId, setPlacingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;
  const close = () => setOpen(false);

  const runSearch = async () => {
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    setError(null);
    try {
      const { results: r } = await searchStock(q, { perPage: 24 });
      setResults(r);
      if (r.length === 0) setError('No results.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed.');
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const place = async (r: StockResult) => {
    setPlacingId(r.sourceId);
    setError(null);
    try {
      const id = await importStockAsset(r.fullUrl);
      const at = (r.attribution ?? '').replace(/"/g, '&quot;');
      const html =
        `<img src="asset://${id}" alt="${at || 'stock photo'}"` +
        `${r.attribution ? ` data-attribution="${at}"` : ''}` +
        ` style="display:block;max-width:100%;height:auto" />`;
      const origin = placeOrigin();
      const cmd: Command = {
        t: 'frame.create', id: `frame_stock_${Date.now()}`,
        x: origin.x, y: origin.y, w: 400, title: 'Stock photo', html,
      };
      useCanvasStore.getState().dispatch(cmd);
      invalidateCanvas();
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not place photo.');
    } finally {
      setPlacingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/30" onClick={close}>
      <div
        className="w-[560px] max-h-[80vh] rounded-[8px] bg-[var(--color-surface)] shadow-[var(--shadow-2)] border border-[var(--color-border)] p-4 font-sans text-[13px] flex flex-col gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div className="text-[var(--color-text)] font-medium">Stock photos</div>
          <button
            className="p-1 rounded-[4px] text-[var(--color-text-muted)] hover:bg-[var(--color-menu-hover)] hover:text-[var(--color-text)]"
            onClick={close}
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex-1 flex items-center gap-2 rounded-[4px] bg-[var(--color-input-bg)] border border-[var(--color-input-border)] px-2 py-1">
            <Search size={14} className="text-[var(--color-text-muted)]" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void runSearch(); }}
              placeholder="Search Pixabay, Openverse, Pexels…"
              className="flex-1 bg-transparent text-[var(--color-text)] outline-none placeholder:text-[var(--color-text-muted)]"
            />
          </div>
          <button
            className="rounded-[4px] bg-[var(--color-accent)] px-3 py-1.5 text-white disabled:opacity-50"
            disabled={searching || !query.trim()}
            onClick={() => void runSearch()}
          >
            {searching ? 'Searching…' : 'Search'}
          </button>
        </div>

        {error && <div className="text-[var(--color-text-muted)]">{error}</div>}

        <div className="overflow-auto grid grid-cols-3 gap-2 pr-1">
          {results.map((r) => (
            <button
              key={`${r.source}-${r.sourceId}`}
              className="group relative flex flex-col gap-1 text-left rounded-[6px] overflow-hidden border border-[var(--color-border)] hover:border-[var(--color-accent)] disabled:opacity-50"
              onClick={() => void place(r)}
              disabled={placingId !== null}
              title={r.attribution || `${r.source} · ${r.license}`}
            >
              <img
                src={r.thumbUrl}
                alt={r.attribution || 'stock photo'}
                loading="lazy"
                className="w-full h-[96px] object-cover bg-[var(--color-surface-2)]"
              />
              {placingId === r.sourceId && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/40 text-white text-[12px]">
                  Placing…
                </span>
              )}
              {r.attribution && (
                <span className="px-1.5 pb-1 text-[11px] leading-tight text-[var(--color-text-muted)] truncate">
                  {r.attribution}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
